import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Folder, MapPin, Plus, X } from 'lucide-react';

import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

export interface TreePickerNode { id: string; name: string; parentId?: string | null; description?: string; hasChildren?: boolean }
export interface TreePickerProps {
  nodes: TreePickerNode[]; label: string; selected: string[]; onChange: (ids: string[]) => void;
  singleSelect?: boolean; hideLabel?: boolean; pillTrigger?: boolean; help?: string; emptyText?: string;
  onSearchChange?: (search: string) => void; searchMatchIds?: string[];
  onExpand?: (id: string) => void; loading?: boolean; loadError?: boolean; onRetry?: () => void;
}
function nodePath(id: string, nodes: TreePickerNode[]) {
  const parts: string[] = [];
  const seen = new Set<string>();
  let node = nodes.find(n => n.id === id);
  while (node && !seen.has(node.id)) {
    seen.add(node.id); parts.unshift(node.name); node = nodes.find(n => n.id === node?.parentId);
  }
  return parts.join(' / ') || id;
}
export default function TreePicker({ nodes, label, selected, onChange, singleSelect = false, hideLabel = false, pillTrigger = false, help, emptyText, onExpand, loading, loadError, onRetry, onSearchChange, searchMatchIds }: TreePickerProps) {
  const { t: { audience: c } } = useTranslation();
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuHeader = useRef<HTMLDivElement>(null);
  const menuList = useRef<HTMLUListElement>(null);
  const [placement, setPlacement] = useState({ above: false, offset: 40, maxHeight: 400, left: 0, width: 0 });
  const selectedRemove = useRef<HTMLButtonElement>(null);
  const hideTrigger = pillTrigger && singleSelect && selected.length > 0;
  const searchInput = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  function updateSearch(value: string) { setSearch(value); onSearchChange?.(value); }
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    searchInput.current?.focus({ preventScroll: true });
    const outside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  const groups = nodes;
  const byId = new Map(groups.map(n => [n.id, n]));
  const children = new Map<string, TreePickerNode[]>();
  const matches = new Set<string>();
  for (const node of groups) {
    const parent = node.parentId && byId.has(node.parentId) ? node.parentId : '';
    const siblings = children.get(parent) ?? [];
    siblings.push(node); children.set(parent, siblings);
    if (searchMatchIds ? searchMatchIds.includes(node.id) : `${node.name} ${node.description ?? ''}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())) {
      let current: TreePickerNode | undefined = node;
      const seen = new Set<string>();
      while (current && !seen.has(current.id)) {
        seen.add(current.id); matches.add(current.id); current = byId.get(current.parentId ?? '');
      }
    }
  }
  const rows: { node: TreePickerNode; depth: number }[] = [];
  const seen = new Set<string>();
  function visit(parent: string, depth: number) {
    for (const node of children.get(parent) ?? []) {
      if (seen.has(node.id) || (search.trim() && !matches.has(node.id))) continue;
      seen.add(node.id); rows.push({ node, depth });
      if (search.trim() || expanded.has(node.id)) visit(node.id, depth + 1);
    }
  }
  visit('', 0);
  useLayoutEffect(() => {
    if (!open) return;
    // The browser's top layer escapes modal overflow while preserving DOM focus containment.
    const popup = menu.current;
    popup?.showPopover();
    const update = () => {
      if (!trigger.current || !container.current || !menuHeader.current || !menuList.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const bounds = container.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
      const gap = 8;
      const below = Math.max(0, viewportBottom - anchor.bottom - gap * 2);
      const above = Math.max(0, anchor.top - viewportTop - gap * 2);
      const desired = menuHeader.current.offsetHeight + Math.min(menuList.current.scrollHeight, 288) + 2;
      const opensAbove = below < desired && above > below;
      const next = {
        above: opensAbove,
        offset: opensAbove ? window.innerHeight - anchor.top + gap : anchor.bottom + gap,
        left: Math.max(gap, Math.min(bounds.left, window.innerWidth - Math.min(bounds.width, window.innerWidth - gap * 2) - gap)),
        width: Math.min(bounds.width, window.innerWidth - gap * 2),
        maxHeight: opensAbove ? above : below,
      };
      setPlacement(previous => previous.above === next.above && previous.offset === next.offset && previous.maxHeight === next.maxHeight && previous.left === next.left && previous.width === next.width ? previous : next);
    };
    update();
    const observer = new ResizeObserver(update);
    [container.current, trigger.current, menuHeader.current, menuList.current].forEach(element => { if (element) observer.observe(element); });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      if (popup?.matches(':popover-open')) popup.hidePopover();
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [open, rows.length]);

  function close() { setOpen(false); requestAnimationFrame(() => (trigger.current ?? selectedRemove.current)?.focus()); }

  return <div ref={container} className="relative flex flex-col gap-2" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    <span id={`${id}-label`} className="sr-only">{label}</span>
    {!hideTrigger && <div className={`flex items-center gap-3 ${hideLabel ? 'justify-start' : 'justify-between'}`}>
      {!hideLabel && <span className="text-sm font-medium text-gray-700">{label}</span>}
      {pillTrigger ? <button ref={trigger} type="button" aria-label={`${c.add}: ${label}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={`${id}-picker`}
        className="inline-flex cursor-pointer items-center gap-2 rounded bg-gray-100 px-2 py-1 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:cursor-not-allowed"
        onClick={() => { setOpen(!open); updateSearch(''); }}><Plus size={14} />{c.add}<ChevronDown size={14} /></button>
        : <Button ref={trigger} type="button" size="sm" variant="outline" aria-label={`${c.add}: ${label}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={`${id}-picker`}
          onClick={() => { setOpen(!open); updateSearch(''); }}><Plus size={14} className="mr-1" />{c.add}<ChevronDown size={14} className="ml-2" /></Button>}
    </div>}
    {selected.length > 0 && <div className="flex flex-wrap gap-2">
      {selected.map(nodeId => <span key={nodeId} className="inline-flex items-center gap-2 rounded bg-gray-100 px-2 py-1 text-sm text-gray-700">
        {nodePath(nodeId, nodes)}<button ref={selectedRemove} type="button" className="cursor-pointer rounded p-0.5 hover:bg-gray-200" aria-label={`${c.remove}: ${nodePath(nodeId, nodes)}`} onClick={() => { onChange(selected.filter(value => value !== nodeId)); if (pillTrigger) requestAnimationFrame(() => trigger.current?.focus()); }}><X size={14} /></button>
      </span>)}
    </div>}
    {open && <div ref={menu} popover="manual" id={`${id}-picker`} role="dialog" aria-labelledby={`${id}-label`} data-placement={placement.above ? 'top' : 'bottom'}
      style={{ top: placement.above ? undefined : placement.offset, bottom: placement.above ? placement.offset : undefined, left: placement.left, right: 'auto', width: placement.width, maxHeight: placement.maxHeight, margin: 0 }}
      className="fixed z-[100] flex flex-col overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
      <div ref={menuHeader} className="shrink-0 space-y-2 border-b border-gray-100 p-3">
        <div className="flex justify-end"><button type="button" aria-label={c.close} className="cursor-pointer rounded p-1 text-gray-500 hover:bg-gray-100" onClick={close}><X size={16} /></button></div>
        <Input ref={searchInput} aria-label={c.search} placeholder={c.search} value={search} onChange={event => updateSearch(event.target.value)} />
        <p className="text-xs text-gray-500">{help ?? (singleSelect ? c.userGroupPickerHelp : c.groupPickerHelp)}</p>
      </div>
      <ul ref={menuList} className="min-h-0 max-h-72 shrink overflow-auto py-1" aria-label={label}>
        {rows.map(({ node, depth }) => {
          const hasChildren = Boolean(node.hasChildren || children.get(node.id)?.length);
          const isExpanded = Boolean(search.trim()) || expanded.has(node.id);
          const checked = selected.includes(node.id);
          const Icon = hasChildren ? Folder : MapPin;
          return <li key={node.id} className="flex items-center gap-1 pr-2 hover:bg-gray-50" style={{ paddingLeft: 8 + depth * 18 }}>
            {hasChildren ? <button type="button" className="shrink-0 cursor-pointer rounded p-1 text-gray-500 hover:bg-gray-200" aria-label={`${isExpanded ? c.collapseGroup : c.expandGroup}: ${node.name}`} aria-expanded={isExpanded}
              onClick={() => { if (!expanded.has(node.id)) onExpand?.(node.id); setExpanded(previous => { const next = new Set(previous); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next; }); }}>{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button> : <span className="w-6 shrink-0" />}
            <button type="button" aria-pressed={checked} title={nodePath(node.id, nodes)} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded py-2 text-left text-sm" onClick={() => { onChange(checked ? selected.filter(value => value !== node.id) : singleSelect ? [node.id] : [...selected, node.id]); if (singleSelect) close(); }}>
              <Icon size={16} aria-hidden="true" className="shrink-0 text-gray-400" /><span className="min-w-0 flex-1 break-words">{node.name}<span className="ml-2 text-xs text-gray-400">{node.description ?? ''}</span></span>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'}`}>{checked && <Check size={12} />}</span>
            </button>
          </li>;
        })}
        {loading && <li role="status" className="p-3 text-sm text-gray-500">{c.loading}</li>}
        {loadError && <li role="alert" className="p-3 text-sm text-red-600">{c.loadError} <button type="button" className="cursor-pointer underline" onClick={onRetry}>{c.retry}</button></li>}
        {!rows.length && !loading && !loadError && <li className="p-4 text-center text-sm text-gray-500">{emptyText ?? c.noGroups}</li>}
      </ul>
    </div>}
  </div>;
}
